/**
 * Shop orders.
 *
 * Same blueprint as the other modules, with one thing peculiar to this one: an
 * order is a basket, but the WORK is a shipment. A single customer order fans out
 * into one shipment per vendor, each with its own status and its own courier
 * reference, and the order's status is only a rollup of them.
 *
 * So the row leads with what a customer bought and who has to ship it, and the
 * tiles count shipments where the backlog lives — most of all the shipments
 * moving with no tracking on them, which is a parcel the customer cannot see.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Copy,
  FileText,
  IndianRupee,
  MoreHorizontal,
  Package,
  PackageCheck,
  PillBottle,
  Store,
  Timer,
  TruckIcon,
  User,
} from 'lucide-react';
import {
  bool_param,
  enum_param,
  page_param,
  page_size_param,
  sort_param,
  string_param,
  use_url_state,
} from '@/hooks/use-url-state';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { DataTable } from '@/components/data-table/data-table';
import {
  BoolFilter,
  FilterBar,
  SearchInput,
  SelectFilter,
  type ChipInput,
} from '@/components/filters/filter-bar';
import { DateRangeFilter, describe_range } from '@/components/filters/date-range-filter';
import { SavedViews } from '@/components/filters/saved-views';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { PersonAvatar } from '@/components/person-avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { notify } from '@/components/ui/toaster';
import { ago, inr, title_case } from '@/lib/format';
import { export_commerce_orders } from '@/features/commerce-orders/api';
import {
  use_commerce_counts,
  use_commerce_order_list,
} from '@/features/commerce-orders/hooks';
import {
  COMMERCE_ORDER_STATUS_OPTIONS,
  COMMERCE_SORT_FIELDS,
  COMMERCE_STATUS_LABELS,
  COMMERCE_VERTICAL_OPTIONS,
  type CommerceCounts,
  type CommerceOrder,
  type CommerceOrderListFilters,
  type CommerceOrderStatus,
  type CommerceVertical,
} from '@/features/commerce-orders/types';

const SEGMENTS = ['to_ship', 'in_flight', 'prescriptions', 'closed', 'unpaid', 'all'] as const;
type Segment = (typeof SEGMENTS)[number];

const SEGMENT_FILTERS: Record<Segment, Partial<CommerceOrderListFilters>> = {
  // Paid, with a vendor who has not accepted yet. The standing job.
  to_ship: { shipment_status: 'PLACED' },
  in_flight: { statuses: 'ACCEPTED,OUT_FOR_DELIVERY,READY' },
  prescriptions: { prescription_required: 'true', statuses: 'PLACED,ACCEPTED,OUT_FOR_DELIVERY' },
  closed: { statuses: 'COMPLETED,CANCELLED,REJECTED' },
  // Abandoned checkouts, which the default list deliberately hides.
  unpaid: { statuses: 'PENDING_PAYMENT' },
  all: {},
};

const SEGMENT_COPY: Record<Segment, { label: string; title: string; blurb: string }> = {
  to_ship: {
    label: 'To ship',
    title: 'Orders waiting on a vendor',
    blurb: 'Paid, with at least one shipment nobody has accepted yet',
  },
  in_flight: {
    label: 'On the way',
    title: 'Orders in flight',
    blurb: 'Accepted, being packed, or already out for delivery',
  },
  prescriptions: {
    label: 'Prescriptions',
    title: 'Prescription orders',
    blurb: 'Open orders with a medicine that needs a prescription checked',
  },
  closed: {
    label: 'Closed',
    title: 'Closed orders',
    blurb: 'Delivered, cancelled, or rejected by the vendor',
  },
  unpaid: {
    label: 'Not paid',
    title: 'Abandoned checkouts',
    blurb: 'A basket somebody walked away from — no vendor can see these',
  },
  all: { label: 'All', title: 'Shop orders', blurb: 'Every order placed in the shop' },
};

/** One colour per status, used on the list and the detail page. */
export const COMMERCE_STATUS_TONE: Record<
  CommerceOrderStatus,
  'success' | 'warning' | 'neutral' | 'destructive' | 'info' | 'primary'
> = {
  PENDING_PAYMENT: 'neutral',
  PLACED: 'warning',
  ACCEPTED: 'info',
  OUT_FOR_DELIVERY: 'primary',
  READY: 'primary',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
  REJECTED: 'destructive',
};

const PAYMENT_TONE: Record<string, 'success' | 'warning' | 'neutral' | 'destructive'> = {
  CAPTURED: 'success',
  PENDING: 'warning',
  REFUNDED: 'destructive',
  FAILED: 'destructive',
};

/** A shipment that has moved but carries nothing the customer could track. */
function is_untracked(shipment: CommerceOrder['shipments'][number]): boolean {
  return (
    (shipment.status === 'ACCEPTED' || shipment.status === 'OUT_FOR_DELIVERY') &&
    !shipment.tracking_ref
  );
}

export function CommerceOrderListPage() {
  const [segment_raw, set_segment] = use_url_state('segment', enum_param(SEGMENTS));
  const segment: Segment = segment_raw || 'to_ship';
  const copy = SEGMENT_COPY[segment];

  const [q, set_q] = use_url_state('q', string_param);
  const [vertical, set_vertical] = use_url_state(
    'vertical',
    enum_param(COMMERCE_VERTICAL_OPTIONS),
  );
  const [status, set_status] = use_url_state('status', enum_param(COMMERCE_ORDER_STATUS_OPTIONS));
  const [shipment_status, set_shipment_status] = use_url_state(
    'shipment_status',
    enum_param(COMMERCE_ORDER_STATUS_OPTIONS),
  );
  const [prescription, set_prescription] = use_url_state('prescription_required', bool_param);
  const [missing_tracking, set_missing_tracking] = use_url_state('missing_tracking', bool_param);
  const [from_cart, set_from_cart] = use_url_state('from_cart', bool_param);
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);
  const [page, set_page] = use_url_state('page', page_param);
  const [page_size, set_page_size] = use_url_state('page_size', page_size_param);
  const [sort, set_sort] = use_url_state(
    'sort',
    sort_param(COMMERCE_SORT_FIELDS, { by: 'created_at', dir: 'desc' }),
  );

  const reset_page = () => set_page(1);

  const filters: CommerceOrderListFilters = useMemo(
    () => ({
      ...SEGMENT_FILTERS[segment],
      page,
      page_size,
      q: q || undefined,
      vertical: vertical || undefined,
      // A hand-picked status beats the segment's set.
      ...(status ? { statuses: undefined, status } : {}),
      shipment_status: shipment_status || SEGMENT_FILTERS[segment].shipment_status,
      prescription_required:
        segment === 'prescriptions' ? 'true' : prescription || undefined,
      missing_tracking: missing_tracking || undefined,
      from_cart: from_cart || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [
      segment,
      page,
      page_size,
      q,
      vertical,
      status,
      shipment_status,
      prescription,
      missing_tracking,
      from_cart,
      from,
      to,
    ],
  );

  const { data, isLoading, isFetching, error, refetch } = use_commerce_order_list(filters, sort);
  const { data: counts } = use_commerce_counts();

  /* --------------------------------- chips --------------------------------- */

  const bool_chip = (
    key: string,
    label: string,
    value: string,
    yes: string,
    no: string,
    clear: () => void,
  ) =>
    value
      ? {
          key,
          label,
          value: value === 'true' ? yes : no,
          advanced: true,
          on_clear: () => {
            clear();
            reset_page();
          },
        }
      : null;

  const chips: ChipInput[] = [
    q && { key: 'q', label: 'Search', value: q, on_clear: () => set_q('') },
    vertical && {
      key: 'vertical',
      label: 'Kind',
      value: vertical === 'PHARMACY' ? 'Medicines' : 'Food & products',
      on_clear: () => set_vertical(''),
    },
    status && {
      key: 'status',
      label: 'Order',
      value: COMMERCE_STATUS_LABELS[status],
      advanced: true,
      on_clear: () => set_status(''),
    },
    shipment_status &&
      segment !== 'to_ship' && {
        key: 'shipment_status',
        label: 'Shipment',
        value: COMMERCE_STATUS_LABELS[shipment_status],
        advanced: true,
        on_clear: () => set_shipment_status(''),
      },
    segment === 'prescriptions'
      ? null
      : bool_chip('prescription_required', 'Prescription', prescription, 'Needed', 'Not needed', () =>
          set_prescription(''),
        ),
    bool_chip('missing_tracking', 'Tracking', missing_tracking, 'Missing', 'Present', () =>
      set_missing_tracking(''),
    ),
    bool_chip('from_cart', 'Source', from_cart, 'From a cart', 'Buy now', () => set_from_cart('')),
    (from || to) && {
      key: 'date',
      label: 'Placed',
      value: describe_range(from, to),
      on_clear: () => {
        set_from('');
        set_to('');
      },
    },
  ];

  function clear_all() {
    set_q('');
    set_vertical('');
    set_status('');
    set_shipment_status('');
    set_prescription('');
    set_missing_tracking('');
    set_from_cart('');
    set_from('');
    set_to('');
    reset_page();
  }

  const metrics: Metric[] = useMemo(() => build_metrics(counts), [counts]);

  /* -------------------------------- columns -------------------------------- */

  const columns = useMemo<ColumnDef<CommerceOrder>[]>(
    () => [
      {
        id: 'created_at',
        header: 'Placed',
        meta: { sort_key: 'created_at' },
        cell: ({ row }) => {
          const o = row.original;
          return (
            <span className="flex min-w-0 flex-col">
              <span className="whitespace-nowrap font-semibold">{ago(o.created_at)}</span>
              <span className="flex flex-wrap items-center gap-1 text-micro text-muted-foreground">
                <Badge tone="neutral" size="sm">
                  {/* Null vertical means the basket mixed food and medicines. */}
                  {o.vertical === 'PHARMACY'
                    ? 'Medicines'
                    : o.vertical === 'FOOD'
                      ? 'Food & products'
                      : 'Mixed basket'}
                </Badge>
                {o.prescription_required && (
                  <Badge tone="warning" size="sm" dot data-testid="badge-prescription">
                    <PillBottle className="h-3 w-3" /> Rx
                  </Badge>
                )}
              </span>
            </span>
          );
        },
      },
      {
        id: 'items',
        header: 'What they bought',
        enableSorting: false,
        meta: { width: '15rem' },
        cell: ({ row }) => {
          const lines = row.original.shipments.flatMap((s) => s.lines);
          const units = lines.reduce((n, l) => n + l.quantity, 0);
          if (lines.length === 0) {
            return <span className="text-muted-foreground/60">No lines</span>;
          }
          return (
            <span className="flex min-w-0 flex-col">
              {/*
                The first product by name, because "3 items" tells an operator
                nothing when a customer rings up about their order.
              */}
              <span className="line-clamp-1 font-medium" title={lines.map((l) => l.name).join(', ')}>
                {lines[0].name}
                {lines[0].variant_label ? ` · ${lines[0].variant_label}` : ''}
              </span>
              <span className="text-micro text-muted-foreground">
                {lines.length === 1 ? '' : `+${lines.length - 1} more · `}
                {units} {units === 1 ? 'unit' : 'units'}
              </span>
            </span>
          );
        },
      },
      {
        id: 'customer',
        header: 'Customer',
        enableSorting: false,
        cell: ({ row }) => {
          const o = row.original;
          return (
            <span className="flex min-w-0 items-center gap-2">
              <PersonAvatar path={null} name={o.customer_name} seed={o.user_id} size="sm" />
              <span className="flex min-w-0 flex-col">
                <Link
                  to={`/users/${o.user_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="truncate font-medium underline-offset-2 hover:text-primary hover:underline"
                >
                  {o.customer_name ?? 'No name on file'}
                </Link>
                <span className="truncate text-micro text-muted-foreground">
                  {o.city ?? o.customer_phone ?? '—'}
                </span>
              </span>
            </span>
          );
        },
      },
      {
        id: 'shipments',
        header: 'Shipments',
        enableSorting: false,
        meta: { width: '15rem' },
        cell: ({ row }) => {
          const ships = row.original.shipments;
          if (ships.length === 0) {
            return <span className="text-muted-foreground/60">None</span>;
          }
          return (
            <span className="flex min-w-0 flex-col gap-0.5">
              {ships.slice(0, 2).map((s) => (
                <span key={s.id} className="flex min-w-0 items-center gap-1.5">
                  <Badge tone={COMMERCE_STATUS_TONE[s.status]} size="sm" dot>
                    {COMMERCE_STATUS_LABELS[s.status]}
                  </Badge>
                  <span className="truncate text-micro text-muted-foreground">
                    {s.vendor_business_name ?? 'Vendor removed'}
                  </span>
                  {/*
                    A parcel in motion with nothing the customer can look up. The
                    commonest "where is my order" call there is.
                  */}
                  {is_untracked(s) && (
                    <Badge tone="warning" size="sm" data-testid="badge-untracked">
                      no tracking
                    </Badge>
                  )}
                </span>
              ))}
              {ships.length > 2 && (
                <span className="text-micro text-muted-foreground">
                  +{ships.length - 2} more vendor{ships.length - 2 === 1 ? '' : 's'}
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: 'grand_total_inr',
        header: 'Total',
        meta: { align: 'right', sort_key: 'grand_total_inr' },
        cell: ({ row }) => {
          const o = row.original;
          return (
            <span className="flex flex-col items-end">
              <span className="whitespace-nowrap tabular font-semibold">
                {inr(o.grand_total_inr)}
              </span>
              {Number(o.delivery_fee_inr) > 0 && (
                <span className="whitespace-nowrap text-micro text-muted-foreground">
                  incl. {inr(o.delivery_fee_inr)} delivery
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: 'payment',
        header: 'Payment',
        enableSorting: false,
        cell: ({ row }) => {
          const p = row.original.payment;
          if (!p) return <span className="text-muted-foreground/60">None</span>;
          return (
            <Badge tone={PAYMENT_TONE[p.status] ?? 'neutral'} size="sm" dot>
              {title_case(p.status)}
            </Badge>
          );
        },
      },
      {
        id: 'status',
        header: 'Order',
        meta: { sort_key: 'status' },
        cell: ({ row }) => (
          <Badge
            tone={COMMERCE_STATUS_TONE[row.original.status]}
            size="sm"
            dot
            data-testid={`status-${row.original.status}`}
          >
            {COMMERCE_STATUS_LABELS[row.original.status]}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) => {
          const o = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Order actions"
                  data-testid={`row-actions-${o.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <RowAction
                  on_select={() => {
                    void navigator.clipboard.writeText(o.id);
                    notify.success('Order ID copied');
                  }}
                  testid={`copy-${o.id}`}
                >
                  <Copy className="h-4 w-4" /> Copy order ID
                </RowAction>
                <DropdownMenuSeparator />
                <RowAction
                  on_select={() => window.open(`/users/${o.user_id}`, '_self')}
                  testid={`open-customer-${o.id}`}
                >
                  <User className="h-4 w-4" /> Open the customer
                </RowAction>
                {o.shipments[0] && (
                  <RowAction
                    on_select={() =>
                      window.open(`/vendors/${o.shipments[0].vendor_profile_id}`, '_self')
                    }
                    testid={`open-vendor-${o.id}`}
                  >
                    <Store className="h-4 w-4" /> Open the vendor
                  </RowAction>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [],
  );

  return (
    <DashboardLayout
      title={copy.title}
      subtitle={copy.blurb}
      actions={<SavedViews module={`shop-orders:${segment}`} is_filtered={chips.some(Boolean)} />}
      tabs={
        <PageTabs
          current={segment}
          on_change={(v) => {
            set_segment(v as Segment);
            reset_page();
          }}
          tabs={SEGMENTS.map((v) => ({
            value: v,
            label: SEGMENT_COPY[v].label,
            count:
              v === 'to_ship'
                ? counts?.awaiting_vendor
                : v === 'prescriptions'
                  ? counts?.prescription_open
                  : undefined,
          }))}
        />
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      <DataTable<CommerceOrder>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        get_row_id={(o) => o.id}
        page={page}
        page_size={page_size}
        on_page_change={set_page}
        on_page_size_change={(n) => {
          set_page_size(n);
          reset_page();
        }}
        sort={sort}
        on_sort_change={(next) => {
          set_sort(next);
          reset_page();
        }}
        is_loading={isLoading}
        is_fetching={isFetching}
        error={error}
        on_retry={() => void refetch()}
        row_href={(o) => `/commerce-orders/${o.id}`}
        storage_key={`shop-orders:${segment}`}
        export_name="shop-orders"
        on_export_all={() => export_commerce_orders(filters, sort)}
        caption="Shop orders"
        test_id="shop-order-list"
        selectable
        empty={{
          icon: Package,
          title: chips.some(Boolean)
            ? 'No orders match these filters'
            : segment === 'to_ship'
              ? 'Nothing waiting to be shipped'
              : `No ${copy.label.toLowerCase()} orders`,
          description: chips.some(Boolean)
            ? 'Try widening the date range or clearing a filter.'
            : segment === 'to_ship'
              ? 'Every paid shipment has been accepted by its vendor.'
              : 'Orders appear here as customers check out in the shop.',
        }}
        filters={
          <FilterBar
            chips={chips}
            on_clear_all={clear_all}
            advanced={
              <>
                <SelectFilter
                  label="Order status"
                  value={status}
                  on_change={(v) => {
                    set_status(v as CommerceOrderStatus);
                    reset_page();
                  }}
                  options={COMMERCE_ORDER_STATUS_OPTIONS.map((s) => ({
                    value: s,
                    label: COMMERCE_STATUS_LABELS[s],
                  }))}
                  all_label="Any status"
                  test_id="filter-status"
                />
                <SelectFilter
                  label="Shipment status"
                  value={shipment_status}
                  on_change={(v) => {
                    set_shipment_status(v as CommerceOrderStatus);
                    reset_page();
                  }}
                  options={COMMERCE_ORDER_STATUS_OPTIONS.filter(
                    (s) => s !== 'PENDING_PAYMENT',
                  ).map((s) => ({ value: s, label: COMMERCE_STATUS_LABELS[s] }))}
                  all_label="Any shipment"
                  test_id="filter-shipment-status"
                />
                <BoolFilter
                  label="Tracking"
                  value={missing_tracking}
                  on_change={(v) => {
                    set_missing_tracking(v);
                    reset_page();
                  }}
                  true_label="Missing"
                  false_label="Present"
                  test_id="filter-missing-tracking"
                />
                <BoolFilter
                  label="Prescription"
                  value={segment === 'prescriptions' ? 'true' : prescription}
                  on_change={(v) => {
                    set_prescription(v);
                    reset_page();
                  }}
                  true_label="Needed"
                  false_label="Not needed"
                  test_id="filter-prescription"
                />
                <BoolFilter
                  label="Source"
                  value={from_cart}
                  on_change={(v) => {
                    set_from_cart(v);
                    reset_page();
                  }}
                  true_label="From a cart"
                  false_label="Buy now"
                  test_id="filter-from-cart"
                />
              </>
            }
          >
            <SearchInput
              value={q}
              on_change={(next) => {
                set_q(next);
                reset_page();
              }}
              label="Search"
              placeholder="Customer, product, vendor, city or order ID…"
              test_id="filter-q"
            />
            <SelectFilter
              label="Kind"
              value={vertical}
              on_change={(v) => {
                set_vertical(v as CommerceVertical);
                reset_page();
              }}
              options={[
                { value: 'FOOD', label: 'Food & products' },
                { value: 'PHARMACY', label: 'Medicines' },
              ]}
              all_label="Anything"
              test_id="filter-vertical"
            />
            <DateRangeFilter
              from={from}
              to={to}
              on_change={(next) => {
                set_from(next.from);
                set_to(next.to);
                reset_page();
              }}
              label="Placed"
            />
          </FilterBar>
        }
      />
    </DashboardLayout>
  );
}

function build_metrics(counts: CommerceCounts | undefined): Metric[] {
  return [
    {
      key: 'to_ship',
      label: 'Waiting on a vendor',
      value: counts?.awaiting_vendor?.toLocaleString('en-IN'),
      hint: 'Paid, not yet accepted',
      tone: counts && counts.awaiting_vendor > 0 ? 'warning' : 'default',
      to: '/commerce-orders?segment=to_ship',
      icon: PackageCheck,
      loading: !counts,
    },
    {
      key: 'in_flight',
      label: 'On the way',
      value: counts?.in_flight?.toLocaleString('en-IN'),
      to: '/commerce-orders?segment=in_flight',
      icon: TruckIcon,
      loading: !counts,
    },
    {
      key: 'missing_tracking',
      label: 'No tracking',
      // A parcel in motion the customer cannot look up — the "where is my order"
      // call, and it was not visible anywhere.
      value: counts?.missing_tracking?.toLocaleString('en-IN'),
      hint: 'No courier reference',
      tone: counts && counts.missing_tracking > 0 ? 'destructive' : 'default',
      to: '/commerce-orders?segment=all&missing_tracking=true',
      icon: Timer,
      loading: !counts,
    },
    {
      key: 'prescriptions',
      label: 'Prescriptions',
      value: counts?.prescription_open?.toLocaleString('en-IN'),
      hint: 'Open, with a medicine to check',
      tone: counts && counts.prescription_open > 0 ? 'warning' : 'default',
      to: '/commerce-orders?segment=prescriptions',
      icon: FileText,
      loading: !counts,
    },
    {
      key: 'captured',
      label: 'Taken',
      value: counts ? inr(counts.captured_inr) : undefined,
      hint: counts ? `${inr(counts.refunded_inr)} refunded` : undefined,
      to: '/commerce-orders?segment=all',
      icon: IndianRupee,
      loading: !counts,
    },
  ];
}

/**
 * A dropdown item that survives Radix's pointer-based selection.
 *
 * Radix selects on pointerdown/pointerup rather than click, so a plain onClick
 * fires after the menu has begun closing and the row's own navigation wins.
 */
function RowAction({
  children,
  on_select,
  testid,
}: {
  children: React.ReactNode;
  on_select: () => void;
  testid: string;
}) {
  return (
    <DropdownMenuItem
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onSelect={on_select}
      data-testid={testid}
    >
      {children}
    </DropdownMenuItem>
  );
}

export default CommerceOrderListPage;
